import type { GameSettings, Preset, RuleSettings } from './types'

const defaultKeys = {
  up: 'ArrowUp',
  down: 'ArrowDown',
  left: 'ArrowLeft',
  right: 'ArrowRight',
  pause: 'p',
  restart: 'r',
}

const base: GameSettings = {
  version: 1,
  presetId: 'classic-v1',
  rules: {
    columns: 24,
    rows: 24,
    startSpeed: 7,
    speedStep: 1,
    wallMode: 'solid',
    selfCollision: true,
    foodCount: 1,
    growth: 1,
    obstaclePercent: 0,
  },
  appearance: {
    themeId: 'volt',
    background: '#090b09',
    snake: '#c7ff39',
    food: '#f7f7ef',
    grid: '#334032',
    bodyStyle: 'soft',
    glow: 72,
    trail: true,
    particles: true,
    gridOpacity: 28,
  },
  controls: { dpad: 'auto', swipeThreshold: 34, haptics: true, keys: defaultKeys },
  audio: { muted: false, volume: 55 },
  accessibility: { reducedMotion: false, highContrast: false },
}

const clone = <T,>(value: T): T => structuredClone(value)

export const CLASSIC_SETTINGS = clone(base)

type BuiltInChanges = Omit<Partial<GameSettings>, 'rules'> & { rules?: Partial<RuleSettings> }

const createBuiltIn = (id: string, name: string, changes: BuiltInChanges): Preset => {
  const settings = clone(base)
  settings.presetId = id
  settings.rules = { ...settings.rules, ...changes.rules }
  if (changes.appearance) settings.appearance = { ...settings.appearance, ...changes.appearance }
  return { id, name, settings, createdAt: 0 }
}

export const BUILT_IN_PRESETS: Preset[] = [
  { id: 'classic-v1', name: 'Clàssic', settings: clone(base), createdAt: 0 },
  createBuiltIn('zen-v1', 'Zen', {
    rules: { startSpeed: 5, speedStep: 0.25, wallMode: 'wrap', selfCollision: false },
    appearance: { ...base.appearance, themeId: 'ice', snake: '#91e9ff', food: '#ffffff', grid: '#24404a' },
  }),
  createBuiltIn('turbo-v1', 'Turbo', {
    rules: { columns: 20, rows: 20, startSpeed: 12, speedStep: 1.5 },
    appearance: { ...base.appearance, themeId: 'ember', snake: '#ff7356', food: '#fff4d6', grid: '#4a2d28' },
  }),
  createBuiltIn('maze-v1', 'Laberint', {
    rules: { startSpeed: 7, obstaclePercent: 8 },
    appearance: { ...base.appearance, themeId: 'violet', snake: '#bda1ff', food: '#ffffff', grid: '#392f55' },
  }),
]

const clamp = (value: unknown, min: number, max: number, fallback: number) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? Math.min(max, Math.max(min, parsed)) : fallback
}

export function sanitizeSettings(value: unknown): GameSettings {
  if (!value || typeof value !== 'object') return clone(base)
  const raw = value as Partial<GameSettings>
  const rules = raw.rules ?? base.rules
  const appearance = raw.appearance ?? base.appearance
  const controls = raw.controls ?? base.controls
  return {
    version: 1,
    presetId: typeof raw.presetId === 'string' ? raw.presetId.slice(0, 40) : 'custom',
    rules: {
      columns: Math.round(clamp(rules.columns, 12, 40, 24)),
      rows: Math.round(clamp(rules.rows, 12, 40, 24)),
      startSpeed: clamp(rules.startSpeed, 4, 18, 7),
      speedStep: clamp(rules.speedStep, 0, 2, 1),
      wallMode: rules.wallMode === 'wrap' ? 'wrap' : 'solid',
      selfCollision: rules.selfCollision !== false,
      foodCount: Math.round(clamp(rules.foodCount, 1, 5, 1)),
      growth: Math.round(clamp(rules.growth, 1, 5, 1)),
      obstaclePercent: Math.round(clamp(rules.obstaclePercent, 0, 20, 0)),
    },
    appearance: {
      themeId: typeof appearance.themeId === 'string' ? appearance.themeId.slice(0, 24) : 'volt',
      background: validColor(appearance.background, base.appearance.background),
      snake: validColor(appearance.snake, base.appearance.snake),
      food: validColor(appearance.food, base.appearance.food),
      grid: validColor(appearance.grid, base.appearance.grid),
      bodyStyle: appearance.bodyStyle === 'block' || appearance.bodyStyle === 'orb' ? appearance.bodyStyle : 'soft',
      glow: Math.round(clamp(appearance.glow, 0, 100, 72)),
      trail: appearance.trail !== false,
      particles: appearance.particles !== false,
      gridOpacity: Math.round(clamp(appearance.gridOpacity, 0, 100, 28)),
    },
    controls: {
      dpad: controls.dpad === 'on' || controls.dpad === 'off' ? controls.dpad : 'auto',
      swipeThreshold: Math.round(clamp(controls.swipeThreshold, 20, 100, 34)),
      haptics: controls.haptics !== false,
      keys: { ...defaultKeys, ...(controls.keys ?? {}) },
    },
    audio: {
      muted: raw.audio?.muted === true,
      volume: Math.round(clamp(raw.audio?.volume, 0, 100, 55)),
    },
    accessibility: {
      reducedMotion: raw.accessibility?.reducedMotion === true,
      highContrast: raw.accessibility?.highContrast === true,
    },
  }
}

function validColor(value: unknown, fallback: string) {
  return typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value) ? value : fallback
}

export function isOfficialRules(rules: RuleSettings): boolean {
  const official = CLASSIC_SETTINGS.rules
  return (Object.keys(official) as (keyof RuleSettings)[]).every((key) => rules[key] === official[key])
}

export function makeCustom(settings: GameSettings): GameSettings {
  return { ...settings, presetId: isOfficialRules(settings.rules) ? 'classic-v1' : 'custom' }
}

export function encodePreset(settings: GameSettings): string {
  const json = JSON.stringify(sanitizeSettings(settings))
  const bytes = new TextEncoder().encode(json)
  let binary = ''
  bytes.forEach((byte) => { binary += String.fromCharCode(byte) })
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

export function decodePreset(encoded: string): GameSettings | null {
  try {
    const normalized = encoded.replace(/-/g, '+').replace(/_/g, '/')
    const binary = atob(normalized)
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0))
    return sanitizeSettings(JSON.parse(new TextDecoder().decode(bytes)))
  } catch {
    return null
  }
}
